import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { productService } from '../../../services/productService';

const CommunityShowcase = ({ title, subtitle, cta_text, cta_link }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredProject, setHoveredProject] = useState(null);
  const [communityProjects, setCommunityProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const categories = [
    { id: 'all', label: 'All Projects', count: 8 },
    { id: 'architecture', label: 'Architecture', count: 3 },
    { id: 'product', label: 'Product Design', count: 2 },
    { id: 'gaming', label: 'Gaming', count: 1 },
    { id: 'interior', label: 'Interior', count: 2 }
  ];

  useEffect(() => {
    loadCommunityProjects();
  }, []);

  const loadCommunityProjects = async () => {
    try {
      setLoading(true);
      const products = await productService?.getAll({ 
        sortBy: 'popular',
        page: 1,
        itemsPerPage: 8
      });
      
      // Map products to match the expected structure
      const mappedProjects = products?.products?.map(product => ({
        id: product?.id,
        title: product?.name,
        creator: product?.manufacturerName || 'OpenGadget Community',
        creatorAvatar: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2394a3b8"><circle cx="12" cy="8" r="4"/><path d="M12 14c-6.1 0-8 4-8 4v2h16v-2s-1.9-4-8-4z"/></svg>',
        creatorAvatarAlt: `${product?.manufacturerName || 'OpenGadget'} creator`,
        image: product?.thumbnailPath || "/assets/images/community-fallback.jpg",
        alt: product?.description || product?.name,
        likes: Math.floor(Math.random() * 5000) + 1000,
        views: Math.floor(Math.random() * 50000) + 10000,
        comments: Math.floor(Math.random() * 500) + 50,
        category: 'product',
        tags: [product?.categoryName || 'Product', '3D Model', 'Design'],
        featured: product?.isFeatured || false,
        verified: true,
        usedModels: Math.floor(Math.random() * 50) + 5,
        createdDate: new Date(product?.createdAt || Date.now())?.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        })
      })) || [];

      setCommunityProjects(mappedProjects);
      setError('');
    } catch (err) {
      console.error('Error loading community projects:', err);
      setError(err?.message || 'Failed to load community projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (e, productId) => {
    // Prevent navigation if clicking on action buttons
    const isActionButton = e?.target?.closest('button');
    if (isActionButton) {
      e?.stopPropagation();
      return;
    }
    navigate(`/3d-model-viewer?id=${productId}`);
  };

  const filteredProjects = selectedCategory === 'all'
    ? communityProjects
    : communityProjects?.filter(project => project?.category === selectedCategory);

  if (loading) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Icon name="Users" size={16} />
              <span>Loading...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="text-destructive">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Users" size={16} />
            <span>Community Creations</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {title || 'Showcase of Excellence'}
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {subtitle || `Be inspired by the incredible projects created by our talented community.
            See what's possible when creativity meets cutting-edge 3D technology.`}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                selectedCategory === category?.id
                  ? 'bg-accent text-accent-foreground shadow-soft'
                  : 'bg-card text-muted-foreground hover:text-foreground hover:bg-card/80'
              }`}
            >
              {category?.label} 
              <span className="ml-2 text-xs opacity-60">({category?.count})</span>
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {filteredProjects?.map((project) => (
            <div
              key={project?.id}
              className="group bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-floating transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
              onMouseEnter={() => setHoveredProject(project?.id)}
              onMouseLeave={() => setHoveredProject(null)}
              onClick={(e) => handleCardClick(e, project?.id)}
            >
              {/* Image Container */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <Image
                  src={project?.image}
                  alt={project?.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Overlay on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 ${
                  hoveredProject === project?.id ? 'opacity-100' : 'opacity-0'
                }`}>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex gap-2 mb-4">
                      {project?.tags?.slice(0, 3)?.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-white/20 backdrop-blur-sm text-white text-xs rounded-md"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="default" size="sm" iconName="Eye" iconPosition="left">
                        View Project
                      </Button>
                      <Button variant="outline" size="sm" iconName="Heart" iconPosition="left" className="border-white/20 text-white hover:bg-white/10">
                        Like
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                  {project?.featured && (
                    <span className="bg-accent text-accent-foreground px-2 py-1 rounded-lg text-xs font-medium">
                      Featured
                    </span>
                  )}
                </div>

                {/* Models Used */}
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-xs flex items-center space-x-1">
                  <Icon name="Package" size={12} />
                  <span>{project?.usedModels} models</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-lg text-foreground group-hover:text-accent transition-colors">
                    {project?.title}
                  </h3>
                </div>

                {/* Creator Info */}
                <div className="flex items-center space-x-3 mb-4">
                  <Image
                    src={project?.creatorAvatar}
                    alt={project?.creatorAvatarAlt}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-foreground">{project?.creator}</span>
                      {project?.verified && (
                        <Icon name="BadgeCheck" size={14} color="var(--color-accent)" />
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">{project?.createdDate}</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-muted-foreground pt-4 border-t border-border">
                  <div className="flex items-center space-x-1">
                    <Icon name="Heart" size={14} />
                    <span>{project?.likes?.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Eye" size={14} />
                    <span>{project?.views?.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="MessageCircle" size={14} />
                    <span>{project?.comments}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View More Button */}
        <div className="text-center">
          <Link to={cta_link || "/model-catalog"}>
            <Button variant="outline" size="lg" iconName="Grid" iconPosition="left">
              {cta_text || 'Explore Community Gallery'}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CommunityShowcase;